/**
 * SSR-safe localStorage + optional AuthService calls for the v1→v2 migration gate
 * (persist session, pre-login `checkEmail`, authenticated `getMigrationStatus`, middleware redirect target).
 */
import type { CPNavigationInput } from '@common/navigation-layer/types/navigation'
import { MIGRATION_STORAGE_KEY, MigrationStatus, type MigrationSessionData } from '../types/auth'

/** Gate routes — shared by migration middleware and auth-page handlers; redirect helper no-ops when already here. */
export const MIGRATION_GATE_PATHS = ['/expired-password', '/profile-confirmation'] as const
export const MIGRATION_GATE_PATHS_SET = new Set<string>(MIGRATION_GATE_PATHS)

const TERMINAL_STATUSES = new Set<MigrationStatus>([MigrationStatus.MIGRATED, MigrationStatus.ONBOARDED])

const isTerminal = (status?: MigrationStatus | null): boolean => !!status && TERMINAL_STATUSES.has(status)

async function getAuthService() {
  const { AuthService } = await import('../services/auth.service')
  return new AuthService('')
}

const isBrowser = (): boolean => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'

export const MigrationUtility = {
  get(): MigrationSessionData | null {
    if (!isBrowser()) return null
    try {
      const raw = window.localStorage.getItem(MIGRATION_STORAGE_KEY)
      if (!raw) return null
      return JSON.parse(raw) as MigrationSessionData
    } catch (error) {
      console.warn('[MigrationUtility] Failed to read migration data, clearing corrupt entry:', error)
      try {
        window.localStorage.removeItem(MIGRATION_STORAGE_KEY)
      } catch {
        /* ignore */
      }
      return null
    }
  },

  save(partial: Partial<MigrationSessionData>): void {
    if (!isBrowser()) return
    const existing = this.get()
    const next: MigrationSessionData = {
      ...(existing || ({} as MigrationSessionData)),
      ...partial,
      migrationStatus: (partial.migrationStatus ?? existing?.migrationStatus) as MigrationStatus,
    }

    if (!next.migrationStatus || isTerminal(next.migrationStatus)) {
      this.clear()
      return
    }

    try {
      window.localStorage.setItem(MIGRATION_STORAGE_KEY, JSON.stringify(next))
    } catch (error) {
      console.warn('[MigrationUtility] Failed to persist migration data:', error)
    }
  },

  clear(): void {
    if (!isBrowser()) return
    try {
      window.localStorage.removeItem(MIGRATION_STORAGE_KEY)
    } catch (error) {
      console.warn('[MigrationUtility] Failed to clear migration data:', error)
    }
  },

  isPending(): boolean {
    const data = this.get()
    if (!data?.migrationStatus) return false
    return !isTerminal(data.migrationStatus)
  },

  /** Pre-login: `checkEmail` + persist when user exists and status is non-terminal. Errors are swallowed. */
  async migrate(email: string, locationId: string): Promise<void> {
    if (!isBrowser()) return
    if (!email || !locationId) return

    try {
      const authService = await getAuthService()
      const { data } = await authService.checkEmail({ email, locationId })

      if (data?.exists && data.user?.migrationStatus && !isTerminal(data.user.migrationStatus)) {
        this.save({
          migrationStatus: data.user.migrationStatus,
          migrationUserId: data.user._id,
          email,
        })
      }
    } catch (error) {
      console.warn('[MigrationUtility] migrate(email) failed:', error)
    }
  },

  /** Authenticated: sync from server; clears storage when status is missing or terminal. */
  async syncMigrationStatus(): Promise<MigrationStatus | null> {
    if (!isBrowser()) return null
    try {
      const authService = await getAuthService()
      const { data } = await authService.getMigrationStatus()
      const status = data?.migrationStatus

      if (!status || isTerminal(status)) {
        this.clear()
        return status || null
      }

      this.save({ migrationStatus: status, migrationUserId: data?.migrationUserId })
      return status
    } catch (error) {
      console.warn('[MigrationUtility] syncMigrationStatus failed:', error)
      return null
    }
  },

  /**
   * Returns a `CPNavigationInput` for middleware to `return` (so Nuxt cancels the in-flight nav), or `null`.
   */
  async redirectIfPendingMigration(currentPath: string): Promise<CPNavigationInput | null> {
    if (!isBrowser()) return null
    const pathOnly = currentPath.split('?')[0]
    if (MIGRATION_GATE_PATHS_SET.has(pathOnly)) return null

    const status = await this.syncMigrationStatus()
    if (!status || isTerminal(status)) return null

    if (status === MigrationStatus.CREATED) {
      return {
        name: 'auth-expired-password',
        query: { redirect: currentPath },
      }
    }

    if (status === MigrationStatus.PASSWORD_CONFIRMED) {
      return {
        name: 'auth-profile-confirmation',
        query: { redirect: currentPath },
      }
    }

    return null
  },
}

export type { MigrationSessionData }
